import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition } from "./types";

// SUN - the final Campaign world. Same 18x18 board as every prior world.
// Locked by the Sun design-lock pass (three rounds of critical review:
// an initial five-map draft, a revision pass that fixed a diagonal-
// movement black-hole error, a direction-preserved-portal runway audit,
// and a final pass that moved the world's one random portal network from
// MERIDIAN to CORONAL WEAVE after MERIDIAN's network could not clear the
// stricter random-destination safety floor on its own geometry). Every
// obstacle coordinate below is the exact, locked Sun design-pass geometry,
// generated directly from the validated design scripts (never hand-
// retyped) - the same "zero risk of drift from the locked spec" reasoning
// `world4.ts` uses for Saturn's own literal coordinate lists, not a
// re-derivation via the `rect`/`wall` authoring helpers.
//
// Sun deliberately does NOT repeat Saturn's ring/lane vocabulary as a
// single unifying shape: SOLAR GATE is a mostly-sealed perimeter with two
// WORMHOLE launch windows and a few internal solar-flare formations,
// MERIDIAN is a three-lane median layout, EVENT HORIZON is four
// independent quadrant loops around a hazard-guarded hub lane, CORONAL
// WEAVE is an open pinwheel/turbine, and APEX (post-launch gameplay
// revision - see its own comment below) combines asteroid clusters and
// broken stripe barriers around a guarded central BLACK HOLE, hosting
// two independent 3-endpoint random portal networks.
const GRID = { cols: 18, rows: 18 };

export const WORLD_5_LEVELS: LevelDefinition[] = [
  {
    id: "w5-l1",
    name: "SOLAR GATE",
    worldId: "w5",
    levelInWorld: 1,
    order: 21,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 34,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w5,
    // "Solar Perimeter": a mostly-sealed literal boundary with exactly two
    // deliberate WORMHOLE launch windows - horizontal (0,9)<->(17,9) and
    // vertical (10,0)<->(10,17) - plus three internal "solar structure"
    // formations (north flare, south-east cluster, south-west cluster).
    // Under WORMHOLE, "where's the nearest launch window?" is the whole
    // puzzle; with WORMHOLE inactive, leaving through a window is still
    // ordinary boundary death - the perimeter obstacles simply restrict
    // WORMHOLE access to these two pairs, with zero engine change. One
    // long fixed portal remains the only other mechanic.
    //
    // Candidate S-B (post-launch manual-QA revision): the shipped shell
    // (obstacles-only-on-the-literal-boundary, no interior structure)
    // played "too empty, too safe" - manual feedback specifically asked
    // for the WORMHOLE-window concept to stay exactly as-is while the
    // interior gained real route decisions. These three formations were
    // chosen and individually verified to: never touch row 9 or column
    // 10 (the two WORMHOLE corridors, which must stay open end-to-end);
    // never reduce the fixed portal's runway from its original locked
    // values (an early south-east placement at column 13 clipped
    // (13,10)'s down-runway to 1 cell and was moved to avoid that
    // column entirely); and never touch the spawn path or the exit.
    //
    // The exit sits at (16,7), one row off the horizontal WORMHOLE
    // corridor (row 9) - NOT at the more obvious (16,9). Putting it on
    // row 9 was audited and rejected: WORMHOLE-wrapping left from (0,9)
    // lands at (17,9), and the real gate-entry check in `LevelGame.tick()`
    // fires on the very next step regardless of WORMHOLE state (it's
    // checked against the raw, unwrapped next-head cell, before any wrap
    // logic runs) - so an exit at (16,9) would let a player reach it in
    // one wrap plus one step once the gate opens, skipping the entire
    // board. Moving the exit to (16,7) removes that shortcut with no
    // other change. The obstacle at (17,7) directly behind the relocated
    // exit is intentional and harmless: `exitStep()` never computes a new
    // head position or performs any collision check - it freezes the
    // snake's coordinates at gate-entry and removes segments from the
    // front each tick until the level completes - and the same
    // wall-immediately-behind-the-exit shape already ships in Saturn
    // (`world4.ts`'s obstacle at (17,9), one cell past its own (16,9) exit).
    obstacles: [
      { x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 0 }, { x: 3, y: 0 }, { x: 4, y: 0 }, { x: 5, y: 0 }, { x: 6, y: 0 }, { x: 7, y: 0 }, { x: 8, y: 0 }, { x: 9, y: 0 }, { x: 11, y: 0 }, { x: 12, y: 0 }, { x: 13, y: 0 }, { x: 14, y: 0 }, { x: 15, y: 0 }, { x: 16, y: 0 }, { x: 17, y: 0 },
      { x: 0, y: 1 }, { x: 17, y: 1 },
      { x: 0, y: 2 }, { x: 6, y: 2 }, { x: 7, y: 2 }, { x: 8, y: 2 }, { x: 17, y: 2 },
      { x: 0, y: 3 }, { x: 6, y: 3 }, { x: 7, y: 3 }, { x: 8, y: 3 }, { x: 17, y: 3 },
      { x: 0, y: 4 }, { x: 1, y: 4 }, { x: 2, y: 4 }, { x: 15, y: 4 }, { x: 16, y: 4 }, { x: 17, y: 4 },
      { x: 0, y: 5 }, { x: 1, y: 5 }, { x: 2, y: 5 }, { x: 15, y: 5 }, { x: 16, y: 5 }, { x: 17, y: 5 },
      { x: 0, y: 6 }, { x: 17, y: 6 },
      { x: 0, y: 7 }, { x: 17, y: 7 },
      { x: 0, y: 8 }, { x: 17, y: 8 },
      { x: 0, y: 10 }, { x: 17, y: 10 },
      { x: 0, y: 11 }, { x: 17, y: 11 },
      { x: 0, y: 12 }, { x: 11, y: 12 }, { x: 12, y: 12 }, { x: 17, y: 12 },
      { x: 0, y: 13 }, { x: 6, y: 13 }, { x: 7, y: 13 }, { x: 11, y: 13 }, { x: 12, y: 13 }, { x: 17, y: 13 },
      { x: 0, y: 14 }, { x: 6, y: 14 }, { x: 11, y: 14 }, { x: 12, y: 14 }, { x: 17, y: 14 },
      { x: 0, y: 15 }, { x: 17, y: 15 },
      { x: 0, y: 16 }, { x: 17, y: 16 },
      { x: 0, y: 17 }, { x: 1, y: 17 }, { x: 2, y: 17 }, { x: 3, y: 17 }, { x: 4, y: 17 }, { x: 5, y: 17 }, { x: 6, y: 17 }, { x: 7, y: 17 }, { x: 8, y: 17 }, { x: 9, y: 17 }, { x: 11, y: 17 }, { x: 12, y: 17 }, { x: 13, y: 17 }, { x: 14, y: 17 }, { x: 15, y: 17 }, { x: 16, y: 17 }, { x: 17, y: 17 },
    ],
    exit: { x: 16, y: 7 },
    portals: [{ id: "p1", a: { x: 4, y: 7 }, b: { x: 13, y: 10 } }],
    pace: { paceParMs: 217000, pacePointsPerSecond: 2, maxPaceBonus: 102 },
  },
  {
    id: "w5-l2",
    name: "MERIDIAN",
    worldId: "w5",
    levelInWorld: 2,
    order: 22,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 34,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w5,
    // Two median walls split the board into three lanes (west 0-5,
    // center 7-11, east 13-17), each wall with two 2-wide gaps offset
    // from the other wall's gaps - a genuine "which lane, which gap"
    // route-choice puzzle. Two fixed portals offer two independent
    // long diagonal shortcuts. No random network and no BLACK HOLE on
    // this level - see w5-l4 for Sun's one random network.
    obstacles: [
      { x: 6, y: 1 }, { x: 6, y: 2 }, { x: 6, y: 3 }, { x: 6, y: 4 }, { x: 6, y: 7 }, { x: 6, y: 8 }, { x: 6, y: 9 }, { x: 6, y: 10 }, { x: 6, y: 13 },
      { x: 6, y: 14 }, { x: 6, y: 15 }, { x: 6, y: 16 }, { x: 12, y: 1 }, { x: 12, y: 2 }, { x: 12, y: 3 }, { x: 12, y: 4 }, { x: 12, y: 5 }, { x: 12, y: 6 },
      { x: 12, y: 7 }, { x: 12, y: 10 }, { x: 12, y: 11 }, { x: 12, y: 12 }, { x: 12, y: 13 }, { x: 12, y: 16 },
    ],
    exit: { x: 16, y: 9 },
    portals: [
      { id: "p1", a: { x: 2, y: 3 }, b: { x: 15, y: 14 } },
      { id: "p2", a: { x: 2, y: 15 }, b: { x: 15, y: 2 } },
    ],
    pace: { paceParMs: 217000, pacePointsPerSecond: 2, maxPaceBonus: 102 },
  },
  {
    id: "w5-l3",
    name: "EVENT HORIZON",
    worldId: "w5",
    levelInWorld: 3,
    order: 23,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    // Deliberately the lowest target in Sun - geometry (four small
    // single-cell-door loop rings) and the two hub BLACK HOLEs already
    // carry this level's full difficulty; a longer target would only
    // compound it, the exact lesson Saturn's THE RELAY/JUNCTION taught.
    targetLength: 30,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w5,
    // Four independent quadrant loop rings (never nested/concentric),
    // each with two single-cell doors on different walls (never a
    // reversal sack). No portals on this level - every ring interior is
    // too small to give any cell 3+ cardinal runway in all four
    // directions, so a portal here would fail the locked random-network
    // safety floor; portal mastery lives on w5-l2/w5-l4 instead.
    obstacles: [
      { x: 2, y: 2 }, { x: 3, y: 2 }, { x: 4, y: 2 }, { x: 5, y: 2 }, { x: 6, y: 2 }, { x: 7, y: 2 }, { x: 2, y: 7 }, { x: 3, y: 7 }, { x: 4, y: 7 },
      { x: 5, y: 7 }, { x: 6, y: 7 }, { x: 7, y: 7 }, { x: 2, y: 3 }, { x: 2, y: 5 }, { x: 2, y: 6 }, { x: 7, y: 3 }, { x: 7, y: 4 }, { x: 7, y: 6 },
      { x: 10, y: 2 }, { x: 11, y: 2 }, { x: 12, y: 2 }, { x: 13, y: 2 }, { x: 14, y: 2 }, { x: 15, y: 2 }, { x: 10, y: 7 }, { x: 11, y: 7 }, { x: 12, y: 7 },
      { x: 14, y: 7 }, { x: 15, y: 7 }, { x: 10, y: 3 }, { x: 10, y: 4 }, { x: 10, y: 6 }, { x: 15, y: 3 }, { x: 15, y: 4 }, { x: 15, y: 5 }, { x: 15, y: 6 },
      { x: 2, y: 10 }, { x: 3, y: 10 }, { x: 4, y: 10 }, { x: 6, y: 10 }, { x: 7, y: 10 }, { x: 2, y: 15 }, { x: 3, y: 15 }, { x: 4, y: 15 }, { x: 5, y: 15 },
      { x: 6, y: 15 }, { x: 7, y: 15 }, { x: 2, y: 11 }, { x: 2, y: 12 }, { x: 2, y: 13 }, { x: 2, y: 14 }, { x: 7, y: 11 }, { x: 7, y: 12 }, { x: 7, y: 14 },
      { x: 10, y: 10 }, { x: 11, y: 10 }, { x: 12, y: 10 }, { x: 14, y: 10 }, { x: 15, y: 10 }, { x: 10, y: 15 }, { x: 11, y: 15 }, { x: 12, y: 15 }, { x: 13, y: 15 },
      { x: 14, y: 15 }, { x: 15, y: 15 }, { x: 10, y: 11 }, { x: 10, y: 13 }, { x: 10, y: 14 }, { x: 15, y: 11 }, { x: 15, y: 12 }, { x: 15, y: 13 }, { x: 15, y: 14 },
    ],
    exit: { x: 16, y: 9 },
    // Both cells sit in the x=9 hub column, sealing it end to end. The
    // x=8 hub column is a fully clear, continuous, CARDINALLY LEGAL
    // north-south route the entire height of the board - the real safe
    // crossing (never a diagonal "step between (8,8) and (9,9)", which
    // is not a legal move at all).
    blackHoles: [
      { x: 9, y: 8 },
      { x: 9, y: 9 },
    ],
    pace: { paceParMs: 194000, pacePointsPerSecond: 2, maxPaceBonus: 89 },
  },
  {
    id: "w5-l4",
    name: "CORONAL WEAVE",
    worldId: "w5",
    levelInWorld: 4,
    order: 24,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    targetLength: 36,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w5,
    // "Radial Spokes": four blade-shaped walls in pinwheel (rotational,
    // not mirror) symmetry around a small open hub - a turbine/starburst
    // silhouette, never a nested ring (no closed loop anywhere in this
    // obstacle set).
    obstacles: [
      { x: 9, y: 2 }, { x: 9, y: 3 }, { x: 9, y: 4 }, { x: 9, y: 5 }, { x: 9, y: 6 }, { x: 9, y: 7 }, { x: 10, y: 9 }, { x: 11, y: 9 }, { x: 12, y: 9 },
      { x: 13, y: 9 }, { x: 14, y: 9 }, { x: 15, y: 9 }, { x: 8, y: 10 }, { x: 8, y: 11 }, { x: 8, y: 12 }, { x: 8, y: 13 }, { x: 8, y: 14 }, { x: 8, y: 15 },
      { x: 2, y: 8 }, { x: 3, y: 8 }, { x: 4, y: 8 }, { x: 5, y: 8 }, { x: 6, y: 8 }, { x: 7, y: 8 },
    ],
    exit: { x: 16, y: 9 },
    // Sun's ONE random portal network. Exhaustively searched against
    // this exact locked obstacle set for >=3-cell cardinal runway in
    // every direction, >=4 cells from the BLACK HOLE, >=6 from spawn/
    // exit - this specific triple is the global optimum for maximizing
    // the minimum pairwise distance (12; Saturn JUNCTION's own shipped
    // network is 13/13/18 - CORONAL WEAVE comes within 1 cell of it,
    // the best this board's geometry can support). No fixed pair on
    // this level: keeping one was tested and found to force the network
    // into only 2 of the map's 4 safe pockets, collapsing its minimum
    // spacing to 3 - see the design-lock discussion.
    portals: [
      {
        id: "p1",
        mode: "random-network",
        endpoints: [
          { x: 3, y: 3 },
          { x: 4, y: 14 },
          { x: 14, y: 4 },
        ],
      },
    ],
    // Sits in the small 2x2 hub the four blades leave open at dead
    // center - the one cell a player cutting straight through the
    // turbine's core would otherwise take for granted. Three of the
    // four hub cells stay clear (a real, cardinal, unambiguous route
    // through, via the shared corner (8,8)).
    blackHoles: [{ x: 9, y: 9 }],
    pace: { paceParMs: 231000, pacePointsPerSecond: 2, maxPaceBonus: 109 },
  },
  {
    id: "w5-l5",
    name: "APEX",
    worldId: "w5",
    levelInWorld: 5,
    order: 25,
    grid: GRID,
    snakeStart: { x: 2, y: 9 },
    startDirection: "right",
    initialLength: 3,
    // Matches Saturn's own finale (THE RELAY, target 32) - the final
    // Campaign level is difficult through route choice and precision,
    // never through raw target length.
    targetLength: 32,
    baseStepMs: GameConfig.gameplay.worldBaseStepMs.w5,
    // "Balanced Hybrid" (Candidate C - second post-launch gameplay-
    // revision pass): manual QA on the first revision ("Four Guardian
    // Pillars") judged the portal concept correct but the arena itself
    // too empty/weak for a Campaign finale. This redesign was built
    // endpoints-first (an exhaustive search over all six-point layouts
    // with pairwise-distinct rows AND columns found that JUNCTION-class
    // within-network spacing (>=13) and a cross-network floor much above
    // 7 cannot both be achieved on this board - the two networks
    // necessarily spanning nearly the whole arena to hit 13+ internally
    // is what caps how far apart they can also be from EACH OTHER; this
    // is the best point on that trade-off curve, not an under-optimized
    // guess), THEN the obstacle geometry was built around those six
    // points: two visual/gameplay languages together - four medium
    // irregular "asteroid" clusters (NW/NE/SW/SE) plus two broken
    // "stripe" barriers (row 11, row 15), each with 2+-cell-wide gaps,
    // never a 1-cell corridor - plus a small asymmetric 3-cell guard-L
    // around the BLACK HOLE at the exact center, reading as a genuine
    // "guarded danger zone" rather than a bare hazard tile. Network A
    // (west/south/north-east) and Network B (south-west/north/east) each
    // hit within-network minimum spacing 13 (14/13/13 and 13/13/14
    // respectively - exceeding Saturn JUNCTION's own shipped 13/13/18 on
    // the tightest leg); nearest cross-network distance is 7 (the
    // verified best achievable alongside 13+ within-network spacing, per
    // the search above). No two of the six endpoints share a row or
    // column with each other, eliminating any straight-line portal
    // "conveyor belt" - within a network or across them - by
    // construction, not by luck. No fixed pair remains: both networks
    // are independently random, entering one endpoint of a network
    // resolves only to one of that SAME network's other two endpoints,
    // never to the other network.
    obstacles: [
      { x: 2, y: 2 },
      { x: 2, y: 3 },
      { x: 2, y: 4 },
      { x: 5, y: 5 }, { x: 6, y: 5 }, { x: 15, y: 5 }, { x: 16, y: 5 },
      { x: 5, y: 6 }, { x: 6, y: 6 }, { x: 11, y: 6 }, { x: 12, y: 6 }, { x: 15, y: 6 }, { x: 16, y: 6 },
      { x: 12, y: 7 },
      { x: 8, y: 8 },
      { x: 10, y: 9 },
      { x: 9, y: 10 },
      { x: 2, y: 11 }, { x: 3, y: 11 }, { x: 5, y: 11 }, { x: 6, y: 11 }, { x: 7, y: 11 }, { x: 11, y: 11 }, { x: 12, y: 11 }, { x: 15, y: 11 },
      { x: 5, y: 12 }, { x: 9, y: 12 }, { x: 11, y: 12 }, { x: 12, y: 12 }, { x: 13, y: 12 },
      { x: 9, y: 13 }, { x: 11, y: 13 }, { x: 15, y: 13 }, { x: 16, y: 13 },
      { x: 15, y: 14 },
      { x: 2, y: 15 }, { x: 3, y: 15 }, { x: 6, y: 15 }, { x: 7, y: 15 }, { x: 8, y: 15 }, { x: 12, y: 15 }, { x: 15, y: 15 }, { x: 16, y: 15 },
      { x: 9, y: 16 }, { x: 16, y: 16 },
    ],
    exit: { x: 16, y: 9 },
    portals: [
      {
        id: "netA",
        mode: "random-network",
        endpoints: [
          { x: 3, y: 7 },
          { x: 10, y: 14 },
          { x: 13, y: 4 },
        ],
      },
      {
        id: "netB",
        mode: "random-network",
        endpoints: [
          { x: 4, y: 13 },
          { x: 7, y: 3 },
          { x: 14, y: 10 },
        ],
      },
    ],
    // Exactly ONE BLACK HOLE, at the exact center of the arena, flanked
    // (not enclosed) by a small asymmetric 3-cell guard-L - a visibly
    // "guarded" danger zone the player must route around, never a
    // decorative dot. 45 obstacle cells + this 1 BLACK HOLE cell means
    // 279 non-obstacle cells but only 278 ordinarily-safe traversable
    // cells - see world5.balance.test.ts's dedicated safe-connectivity
    // test (obstacles AND the BLACK HOLE both treated as blocking) for
    // the exact distinction from plain obstacle-only connectivity.
    blackHoles: [{ x: 9, y: 9 }],
    pace: { paceParMs: 208000, pacePointsPerSecond: 2, maxPaceBonus: 87 },
  },
];
